import { useState } from "react";
import { useNavigate } from "react-router";
import { useLayoutContext } from "@/components/Layout";
import { KpiCard } from "@/components/KpiCard";
import { KpiGrid } from "@/components/KpiGrid";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { StatusBadge } from "@/components/StatusBadge";
import {
  useRoutingMetrics,
  useRoutingPolicies,
  useActivatePolicy,
} from "@/api/hooks/use-routing";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  formatCount,
  formatPercent,
  formatLatency,
  formatTimestamp,
} from "@/lib/format";

export function Routing() {
  const { tenantId, range, userId } = useLayoutContext();
  const navigate = useNavigate();

  const metrics = useRoutingMetrics(tenantId, range, userId);
  const policies = useRoutingPolicies(tenantId);
  const activate = useActivatePolicy();

  const [confirmPolicyId, setConfirmPolicyId] = useState<string | null>(null);
  const [activateSuccess, setActivateSuccess] = useState<string | null>(null);
  const [activateError, setActivateError] = useState<string | null>(null);

  if (!tenantId) {
    return <EmptyState message="Enter a tenant ID to view routing data" />;
  }

  const isLoading = metrics.isLoading || policies.isLoading;
  const hasError = metrics.isError || policies.isError;

  if (isLoading) {
    return <LoadingSkeleton cards={6} rows={5} />;
  }

  if (hasError) {
    return (
      <ErrorState
        message="Failed to load routing data"
        onRetry={() => {
          metrics.refetch();
          policies.refetch();
        }}
      />
    );
  }

  const m = metrics.data;
  const policyList = policies.data?.policies ?? [];

  function handleActivate(policyId: string) {
    setActivateSuccess(null);
    setActivateError(null);
    activate.mutate(
      { policyId, tenantId },
      {
        onSuccess: () => {
          setConfirmPolicyId(null);
          setActivateSuccess(policyId);
          setTimeout(() => setActivateSuccess(null), 3000);
        },
        onError: (err) => {
          setConfirmPolicyId(null);
          setActivateError(
            err instanceof Error ? err.message : "Failed to activate policy"
          );
          setTimeout(() => setActivateError(null), 5000);
        },
      }
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-fg">Routing</h1>
        <button
          type="button"
          onClick={() => navigate("/routing/dry-run")}
          className="rounded-md bg-electric-blue px-3 py-1.5 text-sm font-medium text-white hover:bg-electric-blue/80 transition-colors"
        >
          Dry Run
        </button>
      </div>

      {/* KPI row */}
      {m && (
        <KpiGrid>
          <KpiCard label="Total Requests" value={formatCount(m.total)} />
          <KpiCard label="Cache Hit Rate" value={formatPercent(m.cache_hit_rate)} />
          <KpiCard label="Errors" value={formatCount(m.errors)} color={m.errors > 0 ? "red" : "default"} />
          <KpiCard label="Blocked" value={formatCount(m.blocks)} color={m.blocks > 0 ? "amber" : "default"} />
          <KpiCard label="Avg Phantm Latency" value={formatLatency(m.avg_pipeline_latency_ms ?? null)} />
        </KpiGrid>
      )}

      {/* Mutation feedback */}
      {activateSuccess && (
        <div role="status" className="rounded-md border border-safe-green/30 bg-safe-green/10 px-4 py-2 text-sm text-safe-green">
          Policy activated successfully.
        </div>
      )}
      {activateError && (
        <div role="alert" className="rounded-md border border-block-red/30 bg-block-red/10 px-4 py-2 text-sm text-block-red">
          {activateError}
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmPolicyId && (
        <ConfirmDialog
          title="Activate Policy"
          message="This will activate this policy and deactivate the currently active policy for this tenant. Continue?"
          confirmLabel="Activate"
          isPending={activate.isPending}
          onConfirm={() => handleActivate(confirmPolicyId)}
          onCancel={() => setConfirmPolicyId(null)}
          variant="primary"
        />
      )}

      {/* Policy table */}
      <section>
        <h2 className="text-sm font-medium text-fg-muted uppercase tracking-wider mb-3">
          Policies
        </h2>
        {policyList.length === 0 ? (
          <EmptyState message="No routing policies found for this tenant" />
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line-subtle bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line-subtle text-left text-xs font-medium text-fg-muted uppercase tracking-wider">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line-subtle">
                {policyList.map((p) => (
                  <tr
                    key={p.policy_id}
                    className="cursor-pointer hover:bg-surface-2 transition-colors"
                    tabIndex={0}
                    onClick={() => navigate(`/routing/${p.policy_id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/routing/${p.policy_id}`);
                      }
                    }}
                  >
                    <td className="px-4 py-3 font-medium text-fg">
                      {p.name}
                    </td>
                    <td className="px-4 py-3 font-mono text-fg-muted">
                      {p.version}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-fg-muted">
                      {formatTimestamp(p.created_at)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {p.status !== "active" && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmPolicyId(p.policy_id);
                          }}
                          className="rounded-md border border-electric-blue/40 px-2 py-1 text-xs text-electric-blue hover:bg-electric-blue/10 transition-colors"
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

    </div>
  );
}
